import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { confirmResetPassword } from 'aws-amplify/auth';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { analytics } from '@/lib/analytics';

const resetPasswordSchema = z
  .object({
    code: z.string().length(6, 'Code must be 6 digits').regex(/^\d+$/, 'Code must contain only numbers'),
    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain uppercase letter')
      .regex(/[a-z]/, 'Password must contain lowercase letter')
      .regex(/[0-9]/, 'Password must contain number')
      .regex(/[^A-Za-z0-9]/, 'Password must contain special character'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

interface ResetPasswordConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  onSuccess?: () => void;
  onBackToEmail?: () => void;
}

export function ResetPasswordConfirmModal({
  open,
  onOpenChange,
  email,
  onSuccess,
  onBackToEmail,
}: ResetPasswordConfirmModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      code: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: ResetPasswordFormValues) => {
    setIsLoading(true);

    try {
      analytics.event('password_reset_confirm_attempt', { email });

      // Call Amplify confirmResetPassword
      await confirmResetPassword({
        username: email,
        confirmationCode: data.code,
        newPassword: data.newPassword,
      });

      analytics.conversion('password_reset_success');
      setIsSuccess(true);

      // Auto-close and redirect to login after 2 seconds
      setTimeout(() => {
        onSuccess?.();
        onOpenChange(false);
        form.reset();
        setIsSuccess(false);
      }, 2000);
    } catch (error: any) {
      console.error('Password reset confirm error:', error);

      if (error.name === 'CodeMismatchException') {
        form.setError('code', {
          type: 'manual',
          message: 'Invalid code. Please check and try again.',
        });
      } else if (error.name === 'ExpiredCodeException') {
        form.setError('code', {
          type: 'manual',
          message: 'Code expired. Please request a new one.',
        });
      } else if (error.name === 'InvalidPasswordException') {
        form.setError('newPassword', {
          type: 'manual',
          message: 'Password does not meet requirements.',
        });
      } else if (error.name === 'LimitExceededException') {
        form.setError('code', {
          type: 'manual',
          message: 'Too many attempts. Please try again later.',
        });
      } else {
        form.setError('code', {
          type: 'manual',
          message: 'Failed to reset password. Please try again.',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {isSuccess ? 'Password reset!' : 'Reset your password'}
          </DialogTitle>
          <DialogDescription>
            {isSuccess
              ? 'Your password has been successfully reset. Redirecting to login...'
              : `Enter the code sent to ${email} and choose a new password`}
          </DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div className="flex flex-col items-center justify-center py-8">
            <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" />
            <p className="text-sm text-muted-foreground">Taking you to login page...</p>
          </div>
        ) : (
          <>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-4">
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Verification Code</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="123456"
                          autoComplete="one-time-code"
                          inputMode="numeric"
                          maxLength={6}
                          disabled={isLoading}
                          className="text-center text-2xl tracking-widest"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            disabled={isLoading}
                            {...field}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            tabIndex={-1}
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                      <p className="text-xs text-muted-foreground mt-1">
                        8+ characters, uppercase, lowercase, number, special character
                      </p>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm Password</FormLabel>
                      <FormControl>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          disabled={isLoading}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting password...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </form>
            </Form>

            <div className="text-center mt-6 space-y-2">
              <Button
                variant="link"
                className="px-0 text-sm h-auto"
                type="button"
                onClick={onBackToEmail}
                disabled={isLoading}
              >
                ← Didn't receive code?
              </Button>
              <p className="text-xs text-muted-foreground">
                Check your spam folder. Codes expire after 24 hours.
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
